import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DynamicSelect from "./DynamicSelect";
import SuggestionPicker from "./SuggestionPicker";
import FicheHistorique from "./FicheHistorique";
import FreeTextTags from "./FreeTextTags";
import RichTextEditor from "./RichTextEditor";
import { FicheApercuBody } from "../Recherche/ModalFiche";

import {
  InstantSearch,
} from 'react-instantsearch';
import { algoliaSearchClient, ALGOLIA_INDEX_FICHES } from "../../api/algolia";
import { CustomAutoComplete } from "../Recherche/CustomAutoComplete";
import { useAuth } from "../../context/AuthContext";
import './FichesAdd.css';

const REACT_APP_API = process.env.REACT_APP_API_URL;

// ✅ Structure vide d'une variante
const emptyVariante = () => ({
  varTerme: "",
  varType: "",
  gcatId: "",
  lgaId: "",
  payId: "",
});

// ✅ Type de terme = EQS_ID (1 vedette, 2 synonyme, 3 équivalent) + pour les équivalents TEA_ID (1 étranger, 2 admis).
// Le <select> porte une valeur composite "eqs-tea" pour les équivalents ("3-1", "3-2"), l'EQS_ID seul sinon.
const EQS_EQUIVALENT = "3";
const TEA_ETRANGER = "1";

// ✅ Statut d'une fiche pas encore enregistrée : reprend FicheWorkflow::STATUT_INITIAL côté Symfony
const STATUT_INITIAL_LIBELLE = "Étude CELF";

// ✅ Libellé du bouton d'action selon le statut cible (STA_ID) ; les transitions autorisées viennent de l'API
const STATUT_ACTIONS = {
  3:  "Passer en étude GE",
  4:  "Abandonner (GE)",
  5:  "Transmettre à la CELF",
  6:  "Passer en étude CELF",
  7:  "Abandonner (CELF)",
  8:  "Publier",
  9:  "Annuler la fiche",
  10: "Renvoyer au GE",
};

const typeTermeValue = (t) =>
    t.status === EQS_EQUIVALENT ? `${EQS_EQUIVALENT}-${t.teaId || TEA_ETRANGER}` : (t.status || "");

// ✅ Icône Tabler (contour) d'une ligne du panneau d'erreurs : cercle d'alerte, ou cercle coché une fois corrigé
function StatusIcon({ ok }) {
  return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
           className="fiche-errors-icon" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"></path>
        {ok
            ? <path d="M9 12l2 2l4 -4"></path>
            : <><path d="M12 8v4"></path><path d="M12 16h.01"></path></>}
      </svg>
  );
}

export default function FichesAdd() {
  // ✅ Mode édition : présence d'un id dans l'URL (/creation_fiche/:id)
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();

  const [sourceSelected, setSourceSelected] = useState('');
  const [dom1Selected, setDom1Selected] = useState('');
  const [dom2Selected, setDom2Selected] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [subDomains, setSubDomains] = useState([]);
  // ✅ Domaines disponibles pour le collège actuellement sélectionné (form.comId)
  const [domainesForCollege, setDomainesForCollege] = useState([]);
  const [ficNum, setFicNum] = useState("");
  const [ficAntIds, setFicAntIds] = useState([]);
  const [aInterrogerIds, setAInterrogerIds] = useState([]);

  // ✅ Libellés des sélections (voir aussi / antonyme / à interroger) pour l'affichage en édition
  const [voirLabels, setVoirLabels] = useState({});
  const [antLabels, setAntLabels] = useState({});
  const [aInterrogerLabels, setAInterrogerLabels] = useState({});

  // ✅ Sous-domaines à réappliquer après le chargement de la liste (édition)
  const pendingSdom = useRef(null);

  // ✅ Types de variantes chargés depuis /api/variate_var
  const [varianteTypes, setVarianteTypes] = useState([]);
  const today = new Date().toISOString().split('T')[0];
  const todayFr = new Date().toLocaleDateString('fr-FR');
  const [form, setForm] = useState({
    ficNum: "",
    comId: "",
    ficDom1: "",
    ficDom2: "",
    ficSdom1: "",
    ficSdom2: "",
    ficDefinition: "",
    ficNote: "",
    ficCommentaire: "",
    ficAttention: "",
    ficSource: "",
    ficIsToponyme: "0",
    ficListe: "",
    ficDate: today,
    ficDateRetrait: "",
    ficDatesug: "",
    ficIssuSug: false,
    ficSugId: null,
    termes: []
  });

  // ✅ Droit de modifier CETTE fiche, en miroir de FicheVoter (backend) : CELF
  // toujours, GE seulement si le collège de la fiche fait partie de sa session
  // (user.comIds, exposé par /api/me), Expert jamais — y compris en création
  // (le collège n'est pas encore figé, mais le rôle, lui, l'est déjà).
  const canEdit = useMemo(() => {
    if (user?.utcRole === "CE") return true;
    if (user?.utcRole === "GE") {
      if (!isEdit) return true; // collège pas encore choisi : le backend validera celui soumis
      return (user.comIds || []).includes(Number(form.comId));
    }
    return false;
  }, [isEdit, user, form.comId]);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState(null);
  // ✅ Statut de la fiche ({ id, libelle } | null) et statuts vers lesquels elle peut passer (édition uniquement)
  const [statut, setStatut] = useState(null);
  const [transitions, setTransitions] = useState([]);
  const [statutBusy, setStatutBusy] = useState(false);
  const [statutError, setStatutError] = useState(null);
  // ✅ Erreurs relevées au dernier envoi refusé, sous forme [[clé, message], …] (null = aucune).
  // Chaque ligne reste affichée dans le panneau et passe au vert une fois le problème corrigé.
  const [errorChecklist, setErrorChecklist] = useState(null);

  // ✅ Chaque terme possède un tableau variantes avec tous les champs
  const [termes, setTermes] = useState([{ terTermeM: "", terNote: "", LguId: "", variantes: [] }]);

  // ✅ Chargement des types de variantes
  useEffect(() => {
    const loadVarianteTypes = async () => {
      try {
        const res = await fetch(`${REACT_APP_API || ""}/api/forme_var_frvs`, { credentials: "include" });
        const data = await res.json();
        setVarianteTypes(data.member || []);
      } catch (e) {
        console.error("Erreur chargement types variantes", e);
      }
    };
    loadVarianteTypes();
  }, []);

  const isValidDomainCombination = () => {
    const domains = [form.ficDom1, form.ficDom2].filter(Boolean).length;
    const subDomainsCount = [form.ficSdom1, form.ficSdom2].filter(Boolean).length;
    return (
        (domains >= 1 && domains <= 2 && subDomainsCount === 0) ||
        (domains === 2 && subDomainsCount === 1) ||
        (domains === 1 && subDomainsCount === 2) ||
        (domains === 1 && subDomainsCount === 1)
    );
  };

  // ✅ Erreurs de saisie : champs obligatoires (*) + règle domaines / sous-domaines.
  // Renvoie { clé: message }, dans l'ordre d'apparition dans le formulaire.
  const getFormErrors = () => {
    const errors = {};
    if (!form.comId) errors.comId = "Le collège est obligatoire.";
    if (!isValidDomainCombination()) errors.domaines = "Combinaison Domaine / Sous-domaine invalide.";
    if (termes.length === 0) errors.termes = "Ajoutez au moins un terme.";
    termes.forEach((t, i) => {
      if (!(t.terTermeM || "").trim()) errors[`terme-${i}`] = `Le terme ${i + 1} est obligatoire.`;
      (t.variantes || []).forEach((v, j) => {
        const prefix = `Terme ${i + 1}, variante ${j + 1} : `;
        if (!(v.varTerme || "").trim()) errors[`varTerme-${i}-${j}`] = `${prefix}la variante est obligatoire.`;
        if (!v.varType) errors[`varType-${i}-${j}`] = `${prefix}le type de variante est obligatoire.`;
      });
    });
    if (!(form.ficDefinition || "").trim()) errors.ficDefinition = "La définition est obligatoire.";
    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    const submitErrors = getFormErrors();
    if (Object.keys(submitErrors).length > 0) {
      setErrorChecklist(Object.entries(submitErrors));
      setLoading(false);
      // Le panneau d'erreurs s'affiche au rendu suivant : on l'amène ensuite à l'écran
      setTimeout(() => {
        document.querySelector("[data-errors-panel]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }
    setErrorChecklist(null);

    try {
      const payload = {
        ...form,
        comId:    form.comId    ? parseInt(form.comId)    : null,
        ficDom1:  form.ficDom1  ? parseInt(form.ficDom1)  : null,
        ficDom2:  form.ficDom2  ? parseInt(form.ficDom2)  : null,
        ficSdom1: form.ficSdom1 ? parseInt(form.ficSdom1) : null,
        ficSdom2: form.ficSdom2 ? parseInt(form.ficSdom2) : null,
        ficSource: sourceSelected ? parseInt(sourceSelected) : null,
        ficNum: ficNum || null,
        ficIssuSug: form.ficIssuSug ? 1 : 0,
        ficSugId: form.ficIssuSug ? (form.ficSugId || null) : null,
        // ✅ Variantes complètes avec tous les champs, filtrées si varTerme vide
        termes: termes
            .filter(t => t.terTermeM.trim() !== "")
            .map(t => ({
              ...t,
              variantes: (t.variantes || [])
                  .filter(v => v.varTerme.trim() !== "")
                  .map(v => ({
                    varTerme: v.varTerme,
                    varType:  v.varType  ? parseInt(v.varType)  : null,
                    gcatId:   v.gcatId   ? parseInt(v.gcatId)   : null,
                    lgaId:    v.lgaId    ? parseInt(v.lgaId)    : null,
                    payId:    v.payId    ? parseInt(v.payId)    : null,
                  })),
            })),
        voirAussi: selectedIds,
        ficAnt: ficAntIds,
        ficAInterroger: aInterrogerIds.map(id => aInterrogerLabels[id]).filter(Boolean),
      };

      const url = isEdit
          ? `${REACT_APP_API || ""}/api/custom/fiche/${id}`
          : `${REACT_APP_API || ""}/api/custom/fiche`;

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Vous devez être connecté pour enregistrer une fiche.");
        }
        const errBody = await res.json().catch(() => ({}));
        const detail = errBody?.detail || errBody?.message || errBody?.['hydra:description'] || JSON.stringify(errBody);
        throw new Error(`${res.status} — ${detail}`);
      }
      // En création on verrouille (une seule création) ; en édition on peut modifier plusieurs fois
      if (!isEdit) {
        setSubmitted(true);
      }
      setMessage(isEdit ? "✅ Fiche modifiée avec succès !" : "✅ Fiche créée avec succès !");
    } catch (err) {
      console.error(err);
      setMessage(`❌ Erreur : ${err.message || "Erreur inconnue"}`);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Changement de statut : indépendant de l'enregistrement du formulaire, le serveur valide la transition
  const handleTransition = async (cible) => {
    setStatutBusy(true);
    setStatutError(null);
    try {
      const res = await fetch(`${REACT_APP_API || ""}/api/custom/fiche/${id}/statut`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statutId: cible.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || body?.error || `Erreur ${res.status}`);
      }
      setStatut(body.statut);
      setTransitions(body.transitions ?? []);
    } catch (err) {
      console.error(err);
      setStatutError(err.message || "Erreur inconnue");
    } finally {
      setStatutBusy(false);
    }
  };

  const fetchSubDomains = async (domId) => {
    if (!domId) return [];
    try {
      const res = await fetch(`${REACT_APP_API || ""}/api/dom_ssdom_dsds?dom.domId=${domId}`, { credentials: "include" });
      const data = await res.json();
      return data.member.map(item => item.ssd);
    } catch (e) {
      console.error("Erreur chargement sous-domaines", e);
      return [];
    }
  };

  useEffect(() => {
    // Un changement de domaine plus récent rend cette réponse obsolète
    let cancelled = false;
    const loadSubDomains = async () => {
      let results = [];
      if (dom1Selected) results = [...results, ...await fetchSubDomains(dom1Selected)];
      if (dom2Selected) results = [...results, ...await fetchSubDomains(dom2Selected)];
      if (cancelled) return;
      const unique = Array.from(new Map(results.map(item => [item.ssdId, item])).values());
      setSubDomains(unique);
      // En édition, on réapplique les sous-domaines de la fiche une fois la liste chargée
      if (pendingSdom.current) {
        const { ficSdom1, ficSdom2 } = pendingSdom.current;
        pendingSdom.current = null;
        setForm(prev => ({ ...prev, ficSdom1, ficSdom2 }));
      } else {
        // On ne retire que les sous-domaines qui n'appartiennent plus à un domaine sélectionné
        const available = new Set(unique.map(sd => String(sd.ssdId)));
        setForm(prev => ({
          ...prev,
          ficSdom1: available.has(String(prev.ficSdom1)) ? prev.ficSdom1 : "",
          ficSdom2: available.has(String(prev.ficSdom2)) ? prev.ficSdom2 : "",
        }));
      }
    };
    loadSubDomains();
    return () => { cancelled = true; };
  }, [dom1Selected, dom2Selected]);

  // ✅ Domaines associés au collège sélectionné (relation Many-to-Many CommissionCom <-> DomaineDom,
  // déjà exposée par GET /api/commission_coms/{id}.domaines — cf. CommissionDomainesList.js)
  const fetchDomainesForCollege = async (comId) => {
    if (!comId) return [];
    try {
      const res = await fetch(`${REACT_APP_API || ""}/api/commission_coms/${comId}`, { credentials: "include" });
      const data = await res.json();
      return data.domaines || [];
    } catch (e) {
      console.error("Erreur chargement domaines du collège", e);
      return [];
    }
  };

  useEffect(() => {
    // Un changement de collège plus récent rend cette réponse obsolète
    let cancelled = false;
    const loadDomainesForCollege = async () => {
      const list = await fetchDomainesForCollege(form.comId);
      if (cancelled) return;
      setDomainesForCollege(list);
      // On ne retire que les domaines sélectionnés qui n'appartiennent plus au collège choisi
      const available = new Set(list.map(d => String(d.domId)));
      if (dom1Selected && !available.has(String(dom1Selected))) {
        setDom1Selected('');
        setForm(prev => ({ ...prev, ficDom1: '' }));
      }
      if (dom2Selected && !available.has(String(dom2Selected))) {
        setDom2Selected('');
        setForm(prev => ({ ...prev, ficDom2: '' }));
      }
    };
    loadDomainesForCollege();
    return () => { cancelled = true; };
  }, [form.comId]);

  const computeFicNum = async (comId) => {
    if (!comId) { setFicNum(""); return; }
    try {
      const res = await fetch(`${REACT_APP_API || ""}/api/custom/fiche/last-id/${comId}`, { credentials: "include" });
      const data = await res.json();
      setFicNum(data.ficNum);
    } catch (e) {
      console.error("Erreur récupération last-id", e);
      setFicNum("???");
    }
  };

  // ─── Handlers termes ───────────────────────────────────────────────────────

  const handleStatusChange = (index, newValue) => {
    const [status, teaId = ""] = newValue.split("-");
    const newTermes = [...termes];
    if (status === "1") {
      const existingVedetteIndex = newTermes.findIndex((t, i) => i !== index && t.status === "1");
      if (existingVedetteIndex !== -1) {
        alert("Attention : Un seul terme peut être la VEDETTE. L'ancien terme vedette a été modifié.");
        newTermes[existingVedetteIndex].status = "2";
      }
    }
    newTermes[index].status = status;
    newTermes[index].teaId = teaId;
    setTermes(newTermes);
  };

  const handleFieldChange = (index, field, value) => {
    const newTermes = [...termes];
    newTermes[index][field] = value;
    setTermes(newTermes);
  };

  const addTerme = () => {
    setTermes([...termes, { terTermeM: "", terNote: "", LguId: "", variantes: [] }]);
  };

  const removeTerme = (index) => {
    setTermes(termes.filter((_, i) => i !== index));
  };

  // ─── Handlers variantes ────────────────────────────────────────────────────

  const addVariante = (termeIndex) => {
    const newTermes = [...termes];
    newTermes[termeIndex].variantes = [...(newTermes[termeIndex].variantes || []), emptyVariante()];
    setTermes(newTermes);
  };

  const updateVarianteField = (termeIndex, varIndex, field, value) => {
    const newTermes = [...termes];
    newTermes[termeIndex].variantes[varIndex][field] = value;
    setTermes(newTermes);
  };

  const removeVariante = (termeIndex, varIndex) => {
    const newTermes = [...termes];
    newTermes[termeIndex].variantes = newTermes[termeIndex].variantes.filter((_, i) => i !== varIndex);
    setTermes(newTermes);
  };

  // ✅ Aperçu de la fiche ────────────────────────────────────────────────────
  // Reconstruit, depuis le formulaire en cours (non enregistré), un objet au
  // même format que le "hit" Algolia attendu par FicheApercuBody/ModalFiche,
  // pour afficher un récap de saisie identique à celui de la recherche.
  const apercu = useMemo(() => {
    const lookup = (storageKey) => {
      try {
        return JSON.parse(localStorage.getItem(storageKey) || "[]");
      } catch {
        return [];
      }
    };
    const findLabel = (list, idKey, labelKey, id) => {
      if (!id) return null;
      const item = list.find(it => String(it[idKey]) === String(id));
      return item ? item[labelKey] : null;
    };

    const domaineDoms = lookup("domaine_doms");
    const sourceSces = lookup("source_sces");
    const langueLgus = lookup("langue_lgus");
    const commissionComs = lookup("commission_coms");

    const termesPrincipaux = termes
        .filter(t => t.status !== EQS_EQUIVALENT && (t.terTermeM || "").trim() !== "")
        .map(t => t.terTermeM.trim());

    const variantes = termes.flatMap(t => (t.variantes || [])
        .filter(v => (v.varTerme || "").trim() !== "")
        .map(v => ({
          varTerme: v.varTerme,
          varTypeLib: findLabel(varianteTypes, "frvId", "frvLibForme", v.varType),
        })));

    const langueEtrangere = termes
        .filter(t => t.status === EQS_EQUIVALENT && t.teaId === TEA_ETRANGER && (t.terTermeM || "").trim() !== "")
        .map(t => {
          const code = findLabel(langueLgus, "lguId", "lguCode", t.LguId);
          return code ? `${t.terTermeM} (${code})` : t.terTermeM;
        });

    const voir = selectedIds
        .map(id => (voirLabels[id] ? `${voirLabels[id]}__${id}` : null))
        .filter(Boolean)
        .join(',');

    const aInterroger = aInterrogerIds.map(id => aInterrogerLabels[id]).filter(Boolean).join(', ');

    return {
      fic_num: ficNum || null,
      commission: findLabel(commissionComs, "comId", "comLib", form.comId),
      date: form.ficDate || null,
      date_suggestion: form.ficDatesug || null,
      terme_m: termesPrincipaux.join(' ou ') || null,
      domaine: [
        findLabel(domaineDoms, "domId", "domLib", form.ficDom1),
        findLabel(domaineDoms, "domId", "domLib", form.ficDom2),
      ].filter(Boolean),
      sous_domaine: [
        findLabel(subDomains, "ssdId", "ssdLib", form.ficSdom1),
        findLabel(subDomains, "ssdId", "ssdLib", form.ficSdom2),
      ].filter(Boolean),
      variantes,
      definition: form.ficDefinition || null,
      note: form.ficNote || null,
      voir: voir || null,
      langue_etrangere: langueEtrangere,
      a_interroger: aInterroger || null,
      commentaire: form.ficCommentaire || null,
      source: findLabel(sourceSces, "sceId", "sceLib", sourceSelected),
    };
  }, [form, termes, selectedIds, voirLabels, aInterrogerIds, aInterrogerLabels, sourceSelected, subDomains, varianteTypes, ficNum]);

  // ✅ Chargement de la fiche existante (mode édition)
  useEffect(() => {
    if (!id) return;

    const loadFiche = async () => {
      try {
        const res = await fetch(`${REACT_APP_API || ""}/api/custom/fiche/${id}`, { credentials: "include" });
        if (!res.ok) throw new Error(`Fiche introuvable (${res.status})`);
        const data = await res.json();

        setForm(prev => ({
          ...prev,
          ficNum:         data.ficNum ?? "",
          comId:          data.comId != null ? String(data.comId) : "",
          ficDom1:        data.ficDom1 != null ? String(data.ficDom1) : "",
          ficDom2:        data.ficDom2 != null ? String(data.ficDom2) : "",
          ficDefinition:  data.ficDefinition ?? "",
          ficNote:        data.ficNote ?? "",
          ficCommentaire: data.ficCommentaire ?? "",
          ficAttention:   data.ficAttention ?? "",
          ficListe:       data.ficListe ?? "",
          ficDate:        data.ficDate ?? prev.ficDate,
          ficDatesug:     data.ficDatesug ?? prev.ficDatesug,
          ficIsToponyme:  data.ficIsToponyme ?? "0",
          ficIssuSug:     Boolean(data.ficIssuSug),
          ficSugId:       data.ficSugId ?? null,
        }));
        setFicNum(data.ficNum ?? "");
        setStatut(data.statut ?? null);
        setTransitions(data.transitions ?? []);
        setSourceSelected(data.ficSource != null ? String(data.ficSource) : "");

        // Sous-domaines : mémorisés puis réappliqués après le chargement de la liste
        pendingSdom.current = {
          ficSdom1: data.ficSdom1 != null ? String(data.ficSdom1) : "",
          ficSdom2: data.ficSdom2 != null ? String(data.ficSdom2) : "",
        };
        setDom1Selected(data.ficDom1 != null ? String(data.ficDom1) : "");
        setDom2Selected(data.ficDom2 != null ? String(data.ficDom2) : "");

        // Termes + variantes
        const loadedTermes = (data.termes || []).map(t => ({
          status:    t.status ?? "1",
          terTermeM: t.terTermeM ?? "",
          gcatId:    t.gcatId != null ? String(t.gcatId) : "",
          lgaId:     t.lgaId != null ? String(t.lgaId) : "",
          payId:     t.payId != null ? String(t.payId) : "",
          LguId:     t.LguId != null ? String(t.LguId) : "",
          teaId:     t.teaId != null ? String(t.teaId) : "",
          terNote:   t.terNote ?? "",
          variantes: (t.variantes || []).map(v => ({
            varTerme: v.varTerme ?? "",
            varType:  v.varType != null ? String(v.varType) : "",
            gcatId:   v.gcatId != null ? String(v.gcatId) : "",
            lgaId:    v.lgaId != null ? String(v.lgaId) : "",
            payId:    v.payId != null ? String(v.payId) : "",
          })),
        }));
        setTermes(loadedTermes.length > 0
            ? loadedTermes
            : [{ terTermeM: "", terNote: "", LguId: "", variantes: [] }]);

        // Voir aussi / antonyme (+ libellés)
        setSelectedIds((data.voirAussi || []).map(String));
        setVoirLabels(data.voirLabels || {});
        setFicAntIds((data.ficAnt || []).map(String));
        setAntLabels(data.ficAntLabels || {});

        // À interroger (+ libellés) — [{ id, label }]
        const aInterroger = data.aInterroger || [];
        setAInterrogerIds(aInterroger.map(a => String(a.id)));
        setAInterrogerLabels(
            Object.fromEntries(aInterroger.map(a => [String(a.id), a.label]))
        );
      } catch (e) {
        console.error(e);
        setMessage(`❌ ${e.message || "Erreur de chargement"}`);
      }
    };

    loadFiche();
  }, [id]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const inputStyle = {
    width: "100%",
    height: "40px",
    padding: "8px 14px",
    borderRadius: "4px 4px 0 0",
    backgroundColor: "#fff",
    border: "1px solid rgb(221, 221, 221)",
    borderBottomColor: "rgb(58, 58, 58)",
    borderBottomStyle: "solid",
    borderBottomWidth: "2px",
    fontSize: 16,
    boxSizing: "border-box",
  };

  // Flèche du <select> natif : voir la classe CSS .ft-select-arrow
  // (FichesAdd.css), qui dessine la flèche et l'inverse quand la
  // liste déroulante est ouverte (pseudo-classe :open).
  const selectStyle = { ...inputStyle };

  // Espacement libellé → champ = 8px (maquette)
  const labelStyle = {
    display: "block",
    marginBottom: 8,
    fontWeight: 600,
    color: '#3A3A3A',
    fontSize: 16
  };

  // Espacement vertical entre champs / zones = 24px (maquette)
  const groupStyle = { marginBottom: 24 };

  // ─── Erreurs de saisie (recalculées à chaque rendu, listées dans le panneau à côté du Collège) ───
  const errors = errorChecklist ? getFormErrors() : {};
  const errorBorder = (key) => (errors[key] ? { borderBottomColor: "#ce0500" } : {});
  // Lignes du panneau : les erreurs du dernier envoi (rouge tant que non corrigées, vert ensuite),
  // plus celles apparues depuis (ex. ligne ajoutée après l'envoi).
  const checklistItems = errorChecklist
      ? [
        ...errorChecklist.map(([key, msg]) => ({ key, msg: errors[key] || msg, fixed: !errors[key] })),
        ...Object.entries(errors)
            .filter(([key]) => !errorChecklist.some(([k]) => k === key))
            .map(([key, msg]) => ({ key, msg, fixed: false })),
      ]
      : [];
  const remainingErrors = checklistItems.filter((item) => !item.fixed).length;

  const grid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
      <div id="terme-add">
        <p>
          <h2 style={{ marginBottom: 40, textAlign: 'Left' }}>Gestion des termes {isEdit ? " - modifier le terme" : " - nouveau terme"}</h2>
        </p>

        <div className="terme-add-grid">
        <div className="terme-add-main">
        {/* INFOS FICHE (lecture seule) */}
        <div className="fiche-info-bar">
          <div className="fiche-info-item">
            <span className="fiche-info-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_7513_36160)">
                  <path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H18C18.5304 5 19.0391 5.21071 19.4142 5.58579C19.7893 5.96086 20 6.46957 20 7V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M16 3V7" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 3V7" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4 11H20" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11 15H12" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 15V18" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_7513_36160">
                    <rect width="24" height="24" fill="white"/>
                  </clipPath>
                </defs>
                  </svg>
              </span>
            <div>
              <div className="fiche-info-label">Date de création</div>
              <div className="fiche-info-value">
                {form.ficDate
                    ? new Date(form.ficDate).toLocaleDateString('fr-FR')
                    : todayFr}
              </div>
            </div>
          </div>

          <div className="fiche-info-item">
            <span className="fiche-info-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_7513_36172)">
    <path d="M3 8H21" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M3 16H21" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M8 3V21" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M16 3V21" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_7513_36172">
      <rect width="24" height="24" fill="white"/>
    </clipPath>
  </defs>
</svg></span>
            <div>
              <div className="fiche-info-label">Numéro de fiche</div>
              <div className="fiche-info-value">{ficNum || "—"}</div>
            </div>
          </div>

          <div className="fiche-info-item">
            <span className="fiche-info-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_7513_36182)">
    <path d="M17 17V4L12 7L7 4V17L12 20L17 17Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_7513_36182">
      <rect width="24" height="24" fill="white"/>
    </clipPath>
  </defs>
</svg></span>
            <div>
              <div className="fiche-info-label">Statut</div>
              <span className="fiche-status-badge">
                {isEdit ? (statut?.libelle ?? "Sans statut") : STATUT_INITIAL_LIBELLE}
              </span>
            </div>
          </div>
        </div>

        {isEdit && canEdit && transitions.length > 0 && (
            <div className="fiche-transitions">
              <span className="fiche-transitions-label">Changer le statut :</span>
              {transitions.map(t => (
                  <button
                      key={t.id}
                      type="button"
                      className="fiche-transition-btn"
                      disabled={statutBusy}
                      onClick={() => handleTransition(t)}
                  >
                    {STATUT_ACTIONS[t.id] ?? t.libelle}
                  </button>
              ))}
            </div>
        )}
        {statutError && (
            <p className="fiche-transitions-error" role="alert">❌ {statutError}</p>
        )}

        <form onSubmit={handleSubmit}>

          {/* PANNEAU D'ERREURS : sur 6 colonnes (moitié de largeur), au-dessus des champs obligatoires */}
          {checklistItems.length > 0 && (
              <div className="row">
                <div className="col-8">
                  <div
                      role="alert"
                      data-errors-panel
                      className={`fiche-errors-panel${remainingErrors === 0 ? " fiche-errors-panel--ok" : ""}`}
                  >
                    <strong>
                      {remainingErrors === 0
                          ? "Toutes les erreurs sont corrigées"
                          : remainingErrors === 1 ? "1 erreur à corriger :" : `${remainingErrors} erreurs à corriger :`}
                    </strong>
                    <ul className="fiche-errors-list">
                      {checklistItems.map(({ key, msg, fixed }) => (
                          <li key={key} className={`fiche-errors-item${fixed ? " fiche-errors-item--ok" : ""}`}>
                            <StatusIcon ok={fixed} />
                            <span>
                              <span className="visually-hidden">{fixed ? "Corrigé : " : "Erreur : "}</span>
                              {msg}
                            </span>
                          </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
          )}

          {/* INFO CHAMPS OBLIGATOIRES */}
          <div className="fiche-required-info">
            <span className="fiche-required-info-icon">i</span>
            Les champs suivis d'un astérisque * sont obligatoires
          </div>

          {/* COMMISSION (le N° de fiche calculé est affiché dans le bandeau ci-dessus) */}
          <div style={groupStyle}>
            <label style={labelStyle}>Collège *</label>
            <DynamicSelect
                storageKey="commission_coms"
                name="comId"
                idKey="comId"
                labelKey="comLib"
                placeholder="Collège"
                value={form.comId}
                onChange={(val) => {
                  setForm(prev => ({ ...prev, comId: val }));
                  // En édition on conserve le numéro existant de la fiche
                  if (!isEdit) computeFicNum(val);
                }}
                style={{ ...inputStyle, ...(isEdit ? { backgroundColor: "#f3f4f6", cursor: "not-allowed" } : {}), ...errorBorder("comId") }}
                disabled={isEdit}
            />
          </div>

          {/* DOMAINES */}
          <label className="titre-bloc">Domaines</label>
          <div className="fiche-required-info">
            <span className="fiche-required-info-icon">i</span>
            Les domaines et sous-domaines doivent répondre à l'une des conditions suivantes : un ou deux domaines sélectionnés et aucun sous-domaine, deux domaines sélectionnés et un seul sous-domaine, un seul domaine sélectionné et deux sous-domaines ou un seul domaine sélectionné et un seul sous-domaine.
          </div>

          <div style={grid}>
            <div style={groupStyle}>
              <label style={labelStyle}>Domaine 1</label>
              <DynamicSelect
                  options={domainesForCollege} name="ficDom1" idKey="domId" labelKey="domLib"
                  placeholder="Domaine 1" value={dom1Selected}
                  onChange={(val) => { setDom1Selected(val); setForm(prev => ({ ...prev, ficDom1: val })); }}
                  style={{ ...inputStyle, ...errorBorder("domaines") }}
              />
            </div>
            <div style={groupStyle}>
              <label style={labelStyle}>Sous-domaine 1</label>
              <select name="ficSdom1" value={form.ficSdom1 || ""} onChange={handleChange} className="ft-select-arrow" style={{ ...selectStyle, ...errorBorder("domaines") }} disabled={subDomains.length === 0}>
                <option value="">{subDomains.length === 0 ? "— Sélectionnez d'abord un domaine —" : "Sélectionner"}</option>
                {subDomains.map(sd => <option key={sd.ssdId} value={sd.ssdId}>{sd.ssdLib}</option>)}
              </select>
            </div>
          </div>

          <div style={grid}>
            <div style={groupStyle}>
              <label style={labelStyle}>Domaine 2</label>
              <DynamicSelect
                  options={domainesForCollege} name="ficDom2" idKey="domId" labelKey="domLib"
                  placeholder="Domaine 2" value={dom2Selected}
                  onChange={(val) => { setDom2Selected(val); setForm(prev => ({ ...prev, ficDom2: val })); }}
                  style={{ ...inputStyle, ...errorBorder("domaines") }}
              />
            </div>
            <div style={groupStyle}>
              <label style={labelStyle}>Sous-domaine 2</label>
              <select name="ficSdom2" value={form.ficSdom2 || ""} onChange={handleChange} className="ft-select-arrow" style={{ ...selectStyle, ...errorBorder("domaines") }} disabled={subDomains.length === 0}>
                <option value="">{subDomains.length === 0 ? "— Sélectionnez d'abord un domaine —" : "Sélectionner"}</option>
                {subDomains.map(sd => <option key={sd.ssdId} value={sd.ssdId}>{sd.ssdLib}</option>)}
              </select>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════
            TERMES + VARIANTES
        ════════════════════════════════════════════════════════════════════ */}
          <div style={{ marginBottom: 20 }}>
            <label className="titre-bloc">Termes</label>

            {termes.map((t, index) => {
              const hasVariantes = (t.variantes || []).length > 0;
              return (
                <div
                    key={index}
                    style={{
                      border: "1px solid #e2e8f0",
                      padding: 24,
                      marginTop: 24,
                      background: "#f8fafc",
                    }}
                >
                  {/* ── Supprimer ce terme (coin haut-droit, cf. maquette) ── */}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <button
                        className="btn-delete"
                        type="button"
                        onClick={() => removeTerme(index)}
                    >
                      Supprimer ✕
                    </button>
                  </div>

                  {/* ── Champs du terme (2 lignes, cf. maquette) ──────────── */}
                  <div style={{ display: "flex", width: "100%", gap: 24, alignItems: "flex-end" }}>
                    <div style={{ flex: "0 0 33.3333%", minWidth: '33%' }}>
                      <label style={labelStyle}>Type de terme</label>
                      <select className="ft-select-arrow" style={selectStyle} value={typeTermeValue(t)} onChange={(e) => handleStatusChange(index, e.target.value)}>
                        <option value="1">VEDETTE</option>
                        <option value="2">SYNONYME</option>
                        <option value="3-1">ÉQUIVALENT ÉTRANGER</option>
                        <option value="3-2">ÉQUIVALENT ADMIS</option>
                      </select>
                    </div>

                    <div style={{ flex: 1, minWidth: 160 }}>
                      <label style={labelStyle}>Terme {index + 1} *</label>
                      <input
                          type="text"
                          placeholder={`Terme ${index + 1}`}
                          value={t.terTermeM}
                          onChange={(e) => handleFieldChange(index, 'terTermeM', e.target.value)}
                          style={{ ...inputStyle, ...errorBorder(`terme-${index}`) }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 24, alignItems: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: "0 0 33.3333%", minWidth: 150 }}>
                      <label style={labelStyle}>Catégorie grammaticale</label>
                      <DynamicSelect storageKey="gramm_cat" idKey="gcatId" labelKey="gcatAbr" placeholder="Catégorie"
                                     value={t.gcatId || ''} onChange={(val) => handleFieldChange(index, 'gcatId', val)} style={inputStyle} />
                    </div>

                    {/* Équivalent : Code langue à la place du Niveau de langue */}
                    <div style={{ flex: 1, minWidth: 140 }}>
                      {t.status === EQS_EQUIVALENT ? (
                          <>
                            <label style={labelStyle}>Code langue</label>
                            <DynamicSelect key="lgu" storageKey="langue_lgus" idKey="lguId" labelKey="lguLib" placeholder="Langue"
                                           value={t.LguId || ''} onChange={(val) => handleFieldChange(index, 'LguId', val)} style={inputStyle} />
                          </>
                      ) : (
                          <>
                            <label style={labelStyle}>Niveau de langue</label>
                            <DynamicSelect key="lga" storageKey="langage_lgas" idKey="lgaId" labelKey="lgaLibCrit" placeholder="Niveau"
                                           value={t.lgaId || ''} onChange={(val) => handleFieldChange(index, 'lgaId', val)} style={inputStyle} />
                          </>
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={labelStyle}>Origine géographique</label>
                      <DynamicSelect storageKey="pays_pays" idKey="payId" labelKey="payLib" placeholder="Origine"
                                     value={t.payId || ''} onChange={(val) => handleFieldChange(index, 'payId', val)} style={inputStyle} />
                    </div>
                  </div>

                  {/* ── Note (Équivalent uniquement) ──────────────────────── */}
                  {t.status === EQS_EQUIVALENT && (
                    <div style={{ marginTop: 24 }}>
                      <label style={labelStyle}>Note</label>
                      <textarea
                          placeholder="Note sur cet équivalent..."
                          value={t.terNote || ""}
                          onChange={(e) => handleFieldChange(index, 'terNote', e.target.value)}
                          style={{ ...inputStyle, minHeight: 60 }}
                      />
                    </div>
                  )}

                  {/* ── Variantes associées : la barre bleue n'apparaît qu'une fois une variante ajoutée ── */}
                  <div style={{
                    marginTop: 24,
                    paddingLeft: hasVariantes ? 24 : 0,
                    borderLeft: hasVariantes ? "3px solid #000091" : "none",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      {hasVariantes && (
                          <div>
                            <span style={{ fontSize: 20, fontWeight: 700, color: "#000091" }}>Variante(s) associée(s) </span>
                            <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 99, fontSize: 11, padding: "1px 8px", fontWeight: 600 }}> {t.variantes.length}</span>
                            </div>
                      )}
                    </div>

                    {(t.variantes || []).map((v, varIndex) => (
                        <div
                            key={varIndex}
                            style={{
                              border: "1px solid #d1d5db",
                              background: "#fff",
                              padding: "24px",
                              marginBottom: 24,
                              position: "relative",
                            }}
                        >
                          {/* Bouton supprimer variante */}
                          <button
                              className="btn-delete"
                              type="button"
                              onClick={() => removeVariante(index, varIndex)}
                              title="Supprimer cette variante"
                          >
                            Supprimer ✕
                          </button>

                          <div style={{ paddingRight: 15 }}>

                            {/* ── Ligne 1 : Type de variante + Variante (mêmes proportions que Type de terme / Terme) ── */}
                            <div style={{ display: "flex", width: "100%", gap: 24, alignItems: "flex-end" }}>

                              {/* Type de variante — select obligatoire */}
                              <div style={{ flex: "0 0 33.3333%", minWidth: 150 }}>
                                <label style={labelStyle}>
                                  Type de variante *
                                </label>
                                <select
                                    value={v.varType}
                                    onChange={(e) => updateVarianteField(index, varIndex, 'varType', e.target.value)}
                                    className="ft-select-arrow"
                                    style={{ ...selectStyle, ...errorBorder(`varType-${index}-${varIndex}`) }}
                                >
                                  <option value="">Choisir</option>
                                  {varianteTypes.map(vt => (
                                      <option key={vt.frvId} value={vt.frvId}>
                                        {vt.frvLibForme}
                                      </option>
                                  ))}
                                </select>
                              </div>

                              {/* Variante — texte obligatoire */}
                              <div style={{ flex: 1, minWidth: 160 }}>
                                <label style={labelStyle}>
                                  Variante *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Saisir la variante"
                                    value={v.varTerme}
                                    onChange={(e) => updateVarianteField(index, varIndex, 'varTerme', e.target.value)}
                                    style={{ ...inputStyle, ...errorBorder(`varTerme-${index}-${varIndex}`) }}
                                />
                              </div>

                            </div>

                            {/* ── Ligne 2 : Catégorie grammaticale + Niveau de langue + Origine géographique ── */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 24 }}>

                              {/* Catégorie grammaticale */}
                              <div>
                                <label style={labelStyle}>Catégorie grammaticale</label>
                                <DynamicSelect
                                    storageKey="gramm_cat" idKey="gcatId" labelKey="gcatLib" placeholder="Choisir"
                                    value={v.gcatId || ''}
                                    onChange={(val) => updateVarianteField(index, varIndex, 'gcatId', val)}
                                    style={inputStyle}
                                />
                              </div>

                              {/* Niveau de langue */}
                              <div>
                                <label style={labelStyle}>Niveau de langue</label>
                                <DynamicSelect
                                    storageKey="langage_lgas" idKey="lgaId" labelKey="lgaLibCrit" placeholder="Choisir"
                                    value={v.lgaId || ''}
                                    onChange={(val) => updateVarianteField(index, varIndex, 'lgaId', val)}
                                    style={inputStyle}
                                />
                              </div>

                              {/* Origine géographique */}
                              <div>
                                <label style={labelStyle}>Origine géographique</label>
                                <DynamicSelect
                                    storageKey="pays_pays" idKey="payId" labelKey="payLib" placeholder="Choisir"
                                    value={v.payId || ''}
                                    onChange={(val) => updateVarianteField(index, varIndex, 'payId', val)}
                                    style={inputStyle}
                                />
                              </div>

                            </div>

                          </div>
                        </div>
                    ))}
                  </div>

                  <button
                      type="button"
                      className="fr-btn--secondary"
                      onClick={() => addVariante(index)}
                      style={{ marginTop: 12 }}
                  >
                    + Ajouter une variante
                  </button>
                </div>
              );
            })}

            <button
                type="button"
                className="fr-btn--secondary"
                onClick={addTerme}
                style={{ marginTop: 12 }}
            >
              + Ajouter une Proposition
            </button>
          </div>

          {/* ANTONYME */}
          <span style={labelStyle}>Antonyme : </span>
          <InstantSearch indexName={ALGOLIA_INDEX_FICHES} searchClient={algoliaSearchClient}>
            <CustomAutoComplete
                indexName={ALGOLIA_INDEX_FICHES} labelField="terme_m" name="ficAnt"
                placeholder="Rechercher Antonyme..."
                selectedIds={ficAntIds} onSelectionChange={setFicAntIds}
                singleSelect={true}
                initialLabels={antLabels}
            />
          </InstantSearch>
          <br />

          {/* DÉFINITION / NOTE */}
          <div style={groupStyle}>
            <label style={labelStyle}>Définition *</label>
            <RichTextEditor name="ficDefinition" value={form.ficDefinition} onChange={handleChange} minHeight={80} style={errorBorder("ficDefinition")} />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>Note</label>
            <RichTextEditor name="ficNote" value={form.ficNote} onChange={handleChange} minHeight={60} />
          </div>

          {/* VOIR AUSSI */}
          <span style={labelStyle}>Référence voir aussi : </span>
          <InstantSearch indexName={ALGOLIA_INDEX_FICHES} searchClient={algoliaSearchClient}>
            <CustomAutoComplete
                indexName={ALGOLIA_INDEX_FICHES} labelField="terme_m" name="voirAussi"
                placeholder="Rechercher Voir aussi..."
                selectedIds={selectedIds}
                onSelectionChange={(ids, labels) => { setSelectedIds(ids); setVoirLabels(labels || {}); }}
                initialLabels={voirLabels}
            />
          </InstantSearch>

          <br />

          <div style={groupStyle}>
            <label style={labelStyle}>Attention</label>
            <RichTextEditor name="ficAttention" value={form.ficAttention} onChange={handleChange} minHeight={60} />
          </div>

          <div style={groupStyle}>
            {/* À interroger — mots-clés en saisie libre (pas une sélection de fiches existantes) */}
            <label style={labelStyle}>À interroger</label>
            <FreeTextTags
                name="ficAInterroger"
                placeholder="Saisir un mot-clé puis Entrée (ex. Banane)"
                selectedIds={aInterrogerIds}
                selectedLabels={aInterrogerLabels}
                onChange={(ids, labels) => { setAInterrogerIds(ids); setAInterrogerLabels(labels || {}); }}
            />
          </div>

          <div style={groupStyle}>
            <label style={labelStyle}>Commentaires</label>
            <RichTextEditor name="ficCommentaire" value={form.ficCommentaire} onChange={handleChange} minHeight={60} />
          </div>

          {/* META */}
          <div style={grid}>
            <div style={groupStyle}>
              <label style={labelStyle}>Numéro de la liste</label>
              <input type="text" name="ficListe" value={form.ficListe || ""} onChange={handleChange} maxLength={8} style={inputStyle} />
            </div>
            <div style={groupStyle}>
              <label style={labelStyle}>Source</label>
              <DynamicSelect storageKey="source_sces" idKey="sceId" labelKey="sceLib" placeholder="Source"
                             value={sourceSelected} onChange={setSourceSelected} style={inputStyle} />
            </div>
          </div>

          {/* CHECKBOX */}
          <div style={{ marginBottom: 20 }}>
            <label>
              <input type="checkbox" name="ficIssuSug" checked={!!form.ficIssuSug} onChange={handleChange} />
              {" "}Issu suggestion
            </label>
            {form.ficIssuSug && (
              <div style={{ marginTop: 8, maxWidth: 420 }}>
                <SuggestionPicker
                  value={form.ficSugId}
                  onChange={(sugId) => setForm(prev => ({ ...prev, ficSugId: sugId }))}
                />
              </div>
            )}
          </div>

          {/* SUBMIT — masqué si l'utilisateur n'a pas le droit de modifier cette fiche (cf. canEdit) */}
          {canEdit && (
              <button
                  type="submit"
                  disabled={loading || submitted}
                  style={{
                    marginTop: 48, padding: "10px 24px 10px 18px",
                    background: (loading || submitted) ? "#999" : "#000091",
                    color: "white", border: "none",
                    cursor: (loading || submitted) ? "not-allowed" : "pointer", fontSize: 16, fontWeight: 600,
                  }}
              >
                {loading
                    ? (isEdit ? "Mise à jour..." : "Création...")
                    : submitted
                        ? (isEdit ? "Fiche modifiée" : "Fiche créée")
                        : (isEdit ? "Mettre à jour la fiche" : "Créer la fiche")}
              </button>
          )}
        </form>

        {message && (
            <p style={{ marginTop: 20, textAlign: "center", fontSize: 15 }}>{message}</p>
        )}
        </div>

        {/* APERÇU DE LA FICHE + HISTORIQUE — colonne latérale, collante à partir de 1680px */}
        <div className="terme-add-aside">
          <div className="terme-add-apercu-box">
            <span className="titre-bloc">Aperçu de la fiche</span>
            <FicheApercuBody hit={apercu} visible="true" />
          </div>

          <div className="terme-add-historique-box">
            <FicheHistorique ficId={id} visible="true" />
          </div>
        </div>
        </div>
      </div>
  );
}